/**
 * Helpers of the «Web» tab: periods, labels and the numbers worked out from
 * what gula_web_overview returns. Pure, so they are easy to test.
 */

export const WEB_URL = "https://guladeagua.es";

export type PeriodKey = "today" | "7d" | "30d" | "90d";

export const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: "today", label: "Hoy" },
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "90d", label: "90 días" },
];

export const periodRange = (key: PeriodKey, now = new Date()) => {
  const to = new Date(now.getTime() + 60_000);
  const from = new Date(now);
  from.setHours(0, 0, 0, 0);
  const days = key === "today" ? 0 : key === "7d" ? 6 : key === "30d" ? 29 : 89;
  from.setDate(from.getDate() - days);
  return { from, to, days: days + 1 };
};

export type WebTotals = {
  sessions: number;
  visitors: number;
  pageviews: number;
  bounce: number;
  avg_seconds: number;
  mobile: number;
  consent: number;
  from_ads: number;
  chats: number;
  view_content: number;
  checkout: number;
  leads: number;
  contact: number;
  service: number;
  purchases: number;
};

export type WebSource = {
  source: string;
  campaign: string | null;
  content: string | null;
  sessions: number;
  pageviews: number;
  checkout: number;
  leads: number;
  purchases: number;
};

export type WebPageRow = {
  path: string;
  views: number;
  sessions: number;
  clicks: number;
  scroll: number | null;
  scroll_75: number | null;
};

export type WebClick = {
  label: string;
  path: string;
  href: string | null;
  clicks: number;
  sessions: number;
};

export type WebDay = {
  day: string;
  sessions: number;
  leads: number;
  checkout: number;
  purchases: number;
};

export type WebOverview = {
  totals: WebTotals;
  now: number;
  days: WebDay[];
  sources: WebSource[];
  devices: Record<string, number>;
  landings: { path: string; sessions: number; bounce: number; leads: number }[];
  pages: WebPageRow[];
  top_clicks: WebClick[];
  events: Record<string, number>;
};

export type WebLiveSession = {
  id: string;
  last_seen_at: string;
  started_at: string;
  device: string | null;
  source: string | null;
  campaign: string | null;
  pageviews: number;
  events: string[];
  chat_messages: number;
  path: string | null;
};

export type WebChatMessage = {
  role: "user" | "assistant";
  content: string;
  at?: string;
};

export type WebChat = {
  id: string;
  session_id: string | null;
  started_at: string;
  updated_at: string;
  page: string | null;
  messages: WebChatMessage[];
  message_count: number;
  callback_offered: boolean;
  lead: boolean;
  utm_source: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  fbclid: boolean | null;
  device: string | null;
  landing_path: string | null;
  client_id: number | null;
  contact_id: number | null;
  client_name: string | null;
};

/** Share as a whole percentage; 0 when there is nothing to divide by. */
export const pct = (part: number, total: number) =>
  total > 0 ? Math.round((100 * part) / total) : 0;

/** 0 → "0 s", 75 → "1 min 15 s", 3700 → "1 h 1 min" */
export const formatDuration = (seconds: number) => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) {
    const rest = s % 60;
    return `${Math.floor(s / 60)} min${rest ? ` ${rest} s` : ""}`;
  }
  return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
};

const SOURCE_LABELS: Record<string, string> = {
  facebook: "Meta (Facebook / Instagram)",
  fb: "Meta (Facebook / Instagram)",
  instagram: "Meta (Facebook / Instagram)",
  ig: "Meta (Facebook / Instagram)",
  meta: "Meta (Facebook / Instagram)",
  "l.facebook.com": "Facebook (enlace)",
  "m.facebook.com": "Facebook (enlace)",
  "lm.facebook.com": "Facebook (enlace)",
  "l.instagram.com": "Instagram (enlace)",
  "google ads": "Google Ads",
  google: "Google",
  "google.com": "Google (búsqueda)",
  "google.es": "Google (búsqueda)",
  "bing.com": "Bing",
  directo: "Directo",
  whatsapp: "WhatsApp",
  "web.whatsapp.com": "WhatsApp",
  email: "Correo",
  correo: "Correo",
  crm: "Correo del CRM",
};

export const sourceLabel = (source: string | null | undefined) => {
  if (!source) return "Directo";
  return SOURCE_LABELS[source.toLowerCase()] ?? source;
};

export const isMetaSource = (source: string | null | undefined) =>
  sourceLabel(source).startsWith("Meta") ||
  /facebook|instagram/i.test(source ?? "");

const PAGE_LABELS: Record<string, string> = {
  "/": "Inicio",
  "/grifo-auxiliar": "Equipo con grifo auxiliar",
  "/grifo-3-vias": "Equipo con grifo 3 vías",
  "/gracias": "Gracias (compra hecha)",
  "/politicas": "Política de privacidad",
  "/aviso-legal": "Aviso legal",
  "/cookies": "Política de cookies",
  "/condiciones": "Condiciones de venta",
};

export const pageLabel = (path: string | null | undefined) =>
  path ? (PAGE_LABELS[path] ?? path) : "—";

export type HeatDevice = "movil" | "escritorio" | "todo";

/** Link that opens the real page with the heat map drawn on top. */
export const heatmapUrl = (
  path: string,
  device: HeatDevice,
  key: string,
  days: number,
) => {
  const params = new URLSearchParams({
    gula_mapa: device,
    dias: String(days),
    k: key,
  });
  return `${WEB_URL}${path}?${params.toString()}`;
};

/** Steps of the purchase path, each with its share of all visits. */
export const funnelSteps = (totals: WebTotals) => {
  const base = totals.sessions;
  return [
    { key: "sessions", label: "Visitas", value: base },
    { key: "view_content", label: "Ven un equipo", value: totals.view_content },
    { key: "checkout", label: "Pulsan comprar", value: totals.checkout },
    { key: "purchases", label: "Compran", value: totals.purchases },
  ].map((step) => ({ ...step, share: pct(step.value, base) }));
};

/** First thing the person asked in a chat, to list conversations. */
export const firstQuestion = (
  messages: WebChatMessage[] | null | undefined,
) => {
  const first = (messages ?? []).find((m) => m.role === "user" && m.content);
  return first ? first.content.replace(/\s+/g, " ").trim() : "";
};

/** Days of the period with no visits still show up (empty bars). */
export const fillDays = (days: WebDay[], from: Date, count: number) => {
  const byDay = new Map(days.map((d) => [d.day, d]));
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(from);
    date.setDate(from.getDate() + index);
    const key = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
    return (
      byDay.get(key) ?? {
        day: key,
        sessions: 0,
        leads: 0,
        checkout: 0,
        purchases: 0,
      }
    );
  });
};
